import { ReactNode, useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { useAuth } from '@/context/AuthContext';
import SWChatbot from '@/components/feature/SWChatbot';

interface AppLayoutProps {
  children: ReactNode;
  title: string;
  breadcrumb?: string[];
}

export default function AppLayout({
  children,
  title,
  breadcrumb,
}: AppLayoutProps) {
  const { user } = useAuth();

  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  const isVictim = user?.role === 'victim';

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;

      setIsDesktop(desktop);

      if (desktop) {
        setMobileSidebar(false);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div className="flex h-screen bg-gray-50 overflow-hidden">

        {/* DESKTOP SIDEBAR */}
        {(!isVictim || isDesktop) && (
          <div className="hidden lg:flex">
            <Sidebar />
          </div>
        )}

        {/* MOBILE SIDEBAR */}
        {isVictim && !isDesktop && mobileSidebar && (
          <>
            <div
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setMobileSidebar(false)}
            />

            <div className="fixed left-0 top-0 bottom-0 z-50">
              <Sidebar />
            </div>
          </>
        )}

        {/* MAIN CONTENT */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* MOBILE HEADER */}
          {isVictim && !isDesktop && (
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">

              <button
                onClick={() => setMobileSidebar(true)}
                className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center"
              >
                <i className="ri-menu-line text-xl text-gray-700" />
              </button>

              <h1 className="font-bold text-gray-800 text-sm">
                {title}
              </h1>

              <div className="w-10 h-10" />
            </div>
          )}

          {/* TOPBAR */}
          {(!isVictim || isDesktop) && (
            <TopBar
              title={title}
              breadcrumb={breadcrumb}
            />
          )}

          {/* PAGE CONTENT */}
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {children}
          </main>

        </div>
      </div>

      {/* FLOATING CHATBOT */}
      {(user?.role === "social_worker") && (
        <SWChatbot />
      )}
    </>
  );
}